import * as ShotSelection from '../classes/ShotSelection';
import Golfer, { PreferenceHandicap, PreferenceConditions, PreferenceConfidence, LoftForFitting, Loft } from '../classes/Golfer'
import Fitter from '../classes/Fitter';
import * as GrindTables from './GrindTables';
import { logDev } from './Globals';
import ISimulator from '../classes/ISimulator';

// const REACT_APP_ALGO_EXCLUDE_BUNKER=false
// const REACT_APP_ALGO_BUNKER_RANK=false
// const REACT_APP_ALGO_V2=false
// const REACT_APP_ALGO_REDUCE_100=false
// const REACT_APP_ALGO_SOFTEN_CONF=false
// const REACT_APP_ALGO_HALVE_EXTREMES=false

const REACT_APP_ALGO_EXCLUDE_BUNKER = true
const REACT_APP_ALGO_BUNKER_RANK = true
const REACT_APP_ALGO_REDUCE_100 = true
const REACT_APP_ALGO_SOFTEN_CONF = true
const REACT_APP_ALGO_HALVE_EXTREMES = true
const REACT_APP_ALGO_56_TO_58 = true

export class BounceGrind {
    constructor(public bounce: string, public grind: string) { }
}

//used to do the raw calculations for grind weighting
export type LoftGrindCalculations = Map<Loft, GrindWeighting[]>;

//need a list of all the grinds per loft and a list of which shots recommended that grind
//each shot needs to save the ranking order and which shot number it is so we can assign a weight to it calculated from the weighting tables
class GrindWeighting {
    constructor(public grind: GrindTables.BounceGrindCombos, public totalWeight: number, public weightPercent: number, public shotWeightings: ShotWeighting[]) { };
}

class NoteReason {
    constructor(public name = "", public value = "") { }
}

// this is the 'user friendly' version of LoftGrindCalculations mapping so it can be displayed on the forms
export class LoftGrindRecommendation {
    constructor(public lineNumber: number, public loft: Loft, public selectedBounce: string, public selectedGrind: string, public averageCarryDistance: number, public gapLine: boolean, public gapSmall: boolean, public gapLarge: boolean,
        public grindWeightings: GrindWeightingRecommendation[], public notes: NoteReason[]) { }
}
// this is the 'user friendly' version of GrindWeigting above so it can be displayed on the forms
class GrindWeightingRecommendation {
    constructor(public bounce: string, public grind: string, public gap: string, public weightPercent: number) { };
}

//the values used to calculate a shot's weighting
//rank order (higher priotity shots get more weighting)
//shot number - based on Greg's order of the different shot variations to take and apply them against his custom weighting array
//weight - the actual weight that will be used in the final recommended grind percentages
//Note: We really only need the final weight, but due to the complexity of these calculations I save the values used to calculate it for troubleshooting
class ShotWeighting {
    constructor(public shotRankOrder: number, public shotNumber: number, public weight: number) { };
}

export type ShotTypeAttackAngleAverage = Map<ShotSelection.ShotType, AttackAngleAverage>;

export class AttackAngleAverage {
    constructor(public totalAttackAngle: number, public numberShots: number) { }
}

const Handicap = new Map<PreferenceHandicap, number>([["0 or Less", 1], ["1-5", 2], ["6-10", 3], ["11-15", 4], ["16-20", 5], ["20+", 6]]);
const CourseConditions = new Map<PreferenceConditions, number>([["Soft", 5], ["Slightly Soft", 4], ["Medium", 3], ["Slightly Firm", 2], ["Firm", 1]]);
const BunkerConfidence = new Map<PreferenceConfidence, number>([["Low", 1], ["Less", 2], ["Medium", 3], ["More", 4], ["High", 5]]);
const WeightAmountRaw = [5, 3, 1, 1, 1, 1, 1, 1, 1, 1];
const WeightAmountRawBunkerPriority = [[5, 3, 1, 1, 1, 1, 2, 2, 2, 2], [4, 3, 2, 1, 1, 1, 1.5, 1.5, 1.5, 1.5], [2, 3, 4, 1, 1, 1, 1, 1, 1, 1], [1, 3, 5, 1, 1, 1, .5, .5, .5, .5]];
const WeightAmountRawLT54 = [2, 1, 1, 1, 1, 1, 1, 1, 1, 1];  //lofts less than 54
const RankOrderMultiplier = [1, .5, .25];

//algo 2: Rule #4 - if user changes bunker confidence on Why Grinds page, run the simulation with the original and the new bunker confidence. Then average out the weights
export const getGrindsWithBunkerConfidence = (golfer: Golfer, fitter: Fitter, simulator: ISimulator): LoftGrindCalculations => {
    let currentGrinds = getGrinds(golfer, fitter, simulator);

    //average out the weights if confidence has changed
    if (REACT_APP_ALGO_SOFTEN_CONF && golfer.origConfidence && golfer.confidence !== golfer.origConfidence) {
        let origGolfer = { ...golfer, confidence: golfer.origConfidence };
        let origGrinds = getGrinds(origGolfer, fitter, simulator);
        //loop through each loft/grind and get the average of the old and new grind weights
        golfer.selectedLofts.forEach(loft => {
            let swingTypeBunker = loft.shots.find(sl => sl.shotType === "Bunker" && sl.selected) !== undefined;
            //only do the softening if this loft includes a bunker shot
            if (swingTypeBunker) {
                let origGrind = origGrinds.get(loft.loft);
                let currentGrind = currentGrinds.get(loft.loft);
                if (origGrind && currentGrind) {
                    //sort by grind b/c they are currently sorted by weight percent and the indices dont' line up
                    origGrind.sort((a, b) => a.grind > b.grind ? 1 : -1);
                    currentGrind.sort((a, b) => a.grind > b.grind ? 1 : -1);

                    for (var ix = 0; ix < currentGrind.length; ix++) {
                        currentGrind![ix].weightPercent = Math.round((currentGrind[ix].weightPercent + origGrind[ix].weightPercent) / 2);
                    }
                }
            }
        })
    }

    //make all percents add up to 100
    golfer.selectedLofts.forEach(loft => {
        let currentGrind = currentGrinds.get(loft.loft);
        if (currentGrind) {
            let total = 0;
            let totalAttempts = 0;  //guard check in case there is an edge case where the while loop never ends
            while (total !== 100 && totalAttempts < 10) {
                total = 0;
                totalAttempts++;
                let lastNonZeroIndex = 0;
                //sort by weight so the highest grind is at the top
                currentGrind!.sort((a, b) => b.weightPercent - a.weightPercent);
                //get the total weight for this grind
                currentGrind.forEach((g, ix) => {
                    if (g.weightPercent > 0) {
                        total += g.weightPercent;
                        lastNonZeroIndex = ix;
                    }
                })
                //add missing percents to the top ranking grind. If total === 0 then no shots have been taken yet, so don't force the first shot to 100%
                if (total > 0 && total < 100) {
                    let diff = 100 - total;
                    currentGrind[0].weightPercent = currentGrind[0].weightPercent + diff;
                }
                //subtract additional percents from lowest non-zero grind.
                if (total > 100) {
                    let diff = total - 100;
                    currentGrind[lastNonZeroIndex].weightPercent = Math.max(currentGrind[lastNonZeroIndex].weightPercent - diff, 0);
                }
            }
        }

    })

    return currentGrinds;
}

export const getGrinds = (golfer: Golfer, fitter: Fitter, simulator: ISimulator): LoftGrindCalculations => {
    //get golfer data that will be used throughout entire function
    let fairwayConditions = CourseConditions.get(golfer.courseConditions)!;
    let bunkerConditions = CourseConditions.get(golfer.bunkerConditions)!;
    let bunkerConfidence = BunkerConfidence.get(golfer.confidence)!;
    let avgHandicapLT10 = Handicap.get(golfer.handicap)!.valueOf() <= 3;
    let avgHandicapLT5 = Handicap.get(golfer.handicap)!.valueOf() <= 2;

    let attackAngleAverages: ShotTypeAttackAngleAverage = new Map([["Full", new AttackAngleAverage(0, 0)], ["Pitch", new AttackAngleAverage(0, 0)], ["Open Face", new AttackAngleAverage(0, 0)]]);

    // //build out the storage data we'll need for the final calculations
    // let loftGrindCalculations: LoftGrindCalculations = new Map();

    // //build out an empty grinds array to hold the weighting data per loft
    // golfer.selectedLofts.forEach(loft => {
    //     //fill the array of all possible grinds for this loft
    //     //get all possible grinds for this loft
    //     let grindsPerLoft = GrindTables.GrindsPerLoft.get(loft.loft);
    //     let grindWeightings = [] as GrindWeighting[];
    //     grindsPerLoft!.forEach(grind => {
    //         grindWeightings.push(new GrindWeighting(grind, 0, 0, []));
    //     })
    //     loftGrindCalculations.set(loft.loft, grindWeightings);
    // });

    //calculate the average attack angles for each shot type
    //it is calculated across all lofts
    golfer.loftsForFitting.forEach(loft => {
        loft.practiceShots.forEach(shot => {
            if (shot.status === "closed" && shot.data) {
                let aa_sl = simulator.calculateAttackAngleShaftLean(loft.loft, loft.shotType, shot.data!.attackAngle, shot.data!.dynamicLoft, true, false);
                if (aa_sl.attackAngle !== "--") {
                    //update the attack angle object for the current shot type
                    let attackAngle = attackAngleAverages.get(loft.shotType)!;
                    attackAngle.numberShots++;
                    attackAngle.totalAttackAngle += Number(aa_sl.attackAngle);
                }
            }
        })
    });

    //build out the storage data we'll need for the final calculations
    let loftGrindCalculations: LoftGrindCalculations = new Map();

        //loop through all lofts and the selected shot types to find the suggested grinds and calculate the match percentage
        golfer.selectedLofts.forEach(loft => {
            //Algo 2 update. If loft = 56  AND 56 is the highest loft being used this fitting, then use the GrindTable caluculations for 58 loft instead of 56
            var loftForCalculations = loft.loft;
            if (REACT_APP_ALGO_56_TO_58) {
                var maxLoftUsed = [...golfer.loftsForFitting].sort((a, b) => b.loft > a.loft ? 1 : -1)[0];
                if (maxLoftUsed !== undefined) {
                    if (loftForCalculations === "56" && maxLoftUsed.loft === "56") {
                        loftForCalculations = "58";
                    }
                }
            }

            //get all possible grinds for this loft and save a slot for each one in the loftGrindsCalculation collection            
            let grindsPerLoftTable = GrindTables.getGrindsPerLoft(GrindTables.showSM11(fitter.showSm11));            
            let grindsPerLoft = grindsPerLoftTable.get(loftForCalculations);
            let grindWeightings = [] as GrindWeighting[];
            grindsPerLoft!.forEach(grind => {
                grindWeightings.push(new GrindWeighting(grind, 0, 0, []));
            })
            loftGrindCalculations.set(loft.loft, grindWeightings);

            //set shot type flags for the current loft
            let swingTypeBunker = loft.shots.find(sl => sl.shotType === "Bunker" && sl.selected) !== undefined;
            let swingTypeOpenFace = loft.shots.find(sl => sl.shotType === "Open Face" && sl.selected) !== undefined;

            let weightAmountRaw = WeightAmountRaw;
            let technicalThreshold = 1;

            //special weighting formulas when loft < 54
            if (loftForCalculations < "54") {
                weightAmountRaw = WeightAmountRawLT54;
                technicalThreshold = 2;
            } else {
                // algo 2 rule #2 - vary influence of bunker conditions and confidence based on priority
                if ((REACT_APP_ALGO_BUNKER_RANK || false) && swingTypeBunker) {
                    let bunkerPriority = loft.shots.find(sl => sl.shotType === "Bunker")!.priority;
                    logDev(`'bunker priority', ${bunkerPriority}, ${new Date()}`);
                    weightAmountRaw = WeightAmountRawBunkerPriority[bunkerPriority];
                }
            }

            // go through each shot type, and if any practice shots have been taken for a loft then calculate its grind recommendations        
            //Algo 2 update:
            //shot priority now includes bunker with algo 2, but you can't actually take a bunker shot.
            //so we have to remove bunker from the priority calculations b/c this messes up the rankOrderMultiplier array, which only has 3 elements in it.
            let newShotRanking = loft.shots.filter(shot => shot.selected && shot.shotType !== 'Bunker').sort((a, b) => a.priority - b.priority);
            loft.shots.forEach(shot => {
                if (shot.selected) {
                    let shotType = shot.shotType;

                    //get ranking order based on shot type. Will be used in weighting calculation
                    let rankOrder = newShotRanking.findIndex(newShot => newShot.shotType === shot.shotType);

                    let attackAngleMapping = attackAngleAverages.get(shotType)!;
                    //there must be practice shots for this shot type
                    if (attackAngleMapping && attackAngleMapping.numberShots > 0) {
                        let avgAttackAngle = attackAngleMapping.totalAttackAngle / attackAngleMapping.numberShots;

                        //go through Greg's list of types of weighted shots and get the recommended grind for each one

                        //Shot #0 - the unmodified shot  - Note: start with 0 and not 1 since since it's used for an array lookup
                        let shotNumber = 0;
                        let divotType = calculateDivotType(attackAngleMapping.totalAttackAngle / attackAngleMapping.numberShots);
                        let recommendedGrind = calculateGrind(loftForCalculations, divotType, swingTypeBunker, swingTypeOpenFace, fairwayConditions, bunkerConditions, bunkerConfidence, avgHandicapLT10, avgHandicapLT5);
                        //save the weighted shot data
                        let weight = calculateWeight(shotNumber, rankOrder, weightAmountRaw);
                        let shotWeighting = new ShotWeighting(rankOrder, shotNumber, weight);
                        //special rule for 50 and 52 lofts b/c the recommended grinds here are very close
                        if ("50,52".indexOf(loftForCalculations) > -1 && divotType === "Neutral" && fairwayConditions === 3) {
                            shotWeighting.weight = shotWeighting.weight / 2;
                            addShotToGrindCollection(loft.loft, loftGrindCalculations, new BounceGrind("08", "F"), shotWeighting);
                            addShotToGrindCollection(loft.loft, loftGrindCalculations, new BounceGrind("12", "F"), shotWeighting);
                        } else {
                            addShotToGrindCollection(loft.loft, loftGrindCalculations, recommendedGrind, shotWeighting);
                        }

                        //Shot #1 - are they within the technical threshold of a different shot type? 
                        //For example, if they are Steep, but right on the border then also get a recommended grind for Neutral and weight is less
                        shotNumber = 1;
                        let newAttackAngle = 0;
                        if ((-4 + technicalThreshold) > avgAttackAngle && avgAttackAngle > -4) {
                            newAttackAngle = -4.5;  //barely shallow, try neutral
                        } else if (-4 > avgAttackAngle && avgAttackAngle > (-4 - technicalThreshold)) {
                            newAttackAngle = -3.5;  //barely neutral, try shallow
                        } else if (-8 > avgAttackAngle && avgAttackAngle > (-8 - technicalThreshold)) {
                            newAttackAngle = -7.5; //barely steep, try neutral
                        } else if ((-8 + technicalThreshold) > avgAttackAngle && avgAttackAngle > -8) {
                            newAttackAngle = -8.5; //barely neutral, try steep
                        }
                        if (newAttackAngle !== 0) {
                            let newDivotType = calculateDivotType(newAttackAngle);
                            recommendedGrind = calculateGrind(loftForCalculations, newDivotType, swingTypeBunker, swingTypeOpenFace, fairwayConditions, bunkerConditions, bunkerConfidence, avgHandicapLT10, avgHandicapLT5);
                            //save the weighted shot data
                            let weight = calculateWeight(shotNumber, rankOrder, weightAmountRaw);
                            shotWeighting = new ShotWeighting(rankOrder, shotNumber, weight);
                            addShotToGrindCollection(loft.loft, loftGrindCalculations, recommendedGrind, shotWeighting);
                        }

                        //Shot #2 - for higher lofts, toggle bunker play on/off
                        shotNumber = 2;
                        if (loftForCalculations > "52") {
                            recommendedGrind = calculateGrind(loftForCalculations, divotType, !swingTypeBunker, swingTypeOpenFace, fairwayConditions, bunkerConditions, bunkerConfidence, avgHandicapLT10, avgHandicapLT5);
                            //save the weighted shot data
                            weight = calculateWeight(shotNumber, rankOrder, weightAmountRaw);
                            shotWeighting = new ShotWeighting(rankOrder, shotNumber, weight);
                            addShotToGrindCollection(loft.loft, loftGrindCalculations, recommendedGrind, shotWeighting);
                        }

                        //algo 2 rule #1 - for fairway shots, bunker type should be turned off so it doesn't influence the fairway calculations.
                        //this will only be used for shots #3, #4 and #5. For Shots #6 and after, use bunker setting as normal
                        var tempSwingTypeBunker = REACT_APP_ALGO_EXCLUDE_BUNKER ? false : swingTypeBunker;

                        //Shot #3 - for higher lofts, toggle open face play on/off
                        shotNumber = 3;
                        if (loftForCalculations > "56") {
                            recommendedGrind = calculateGrind(loftForCalculations, divotType, tempSwingTypeBunker, !swingTypeOpenFace, fairwayConditions, bunkerConditions, bunkerConfidence, avgHandicapLT10, avgHandicapLT5);
                            //save the weighted shot data
                            weight = calculateWeight(shotNumber, rankOrder, weightAmountRaw);
                            shotWeighting = new ShotWeighting(rankOrder, shotNumber, weight);
                            addShotToGrindCollection(loft.loft, loftGrindCalculations, recommendedGrind, shotWeighting);
                        }

                        //Shot #4 - move fairway conditions up by one
                        shotNumber = 4;
                        if (fairwayConditions < 5) {
                            recommendedGrind = calculateGrind(loftForCalculations, divotType, tempSwingTypeBunker, swingTypeOpenFace, fairwayConditions + 1, bunkerConditions, bunkerConfidence, avgHandicapLT10, avgHandicapLT5);
                            //save the weighted shot data
                            weight = calculateWeight(shotNumber, rankOrder, weightAmountRaw);
                            //algo 2 rule #3 - soften changing fairway conditions to the extreme range
                            if (REACT_APP_ALGO_HALVE_EXTREMES && fairwayConditions === 4) {
                                weight = weight / 2;
                            }
                            shotWeighting = new ShotWeighting(rankOrder, shotNumber, weight);
                            addShotToGrindCollection(loft.loft, loftGrindCalculations, recommendedGrind, shotWeighting);
                        }

                        //Shot #5 - move fairway conditions down by one
                        shotNumber = 5;
                        if (fairwayConditions > 1) {
                            recommendedGrind = calculateGrind(loftForCalculations, divotType, tempSwingTypeBunker, swingTypeOpenFace, fairwayConditions - 1, bunkerConditions, bunkerConfidence, avgHandicapLT10, avgHandicapLT5);
                            //save the weighted shot data
                            weight = calculateWeight(shotNumber, rankOrder, weightAmountRaw);
                            //algo 2 rule #3 - soften changing fairway conditions to the extreme range
                            if (REACT_APP_ALGO_HALVE_EXTREMES && fairwayConditions === 2) {
                                weight = weight / 2;
                            }
                            shotWeighting = new ShotWeighting(rankOrder, shotNumber, weight);
                            addShotToGrindCollection(loft.loft, loftGrindCalculations, recommendedGrind, shotWeighting);
                        }

                        //Shot #6,7 - is this shot used for bunker play? If so, move bunker conditions up/down by 1.
                        if (swingTypeBunker) {
                            if (bunkerConditions < 5) {
                                shotNumber = 6
                                recommendedGrind = calculateGrind(loftForCalculations, divotType, swingTypeBunker, swingTypeOpenFace, fairwayConditions, bunkerConditions + 1, bunkerConfidence, avgHandicapLT10, avgHandicapLT5);
                                //save the weighted shot data
                                weight = calculateWeight(shotNumber, rankOrder, weightAmountRaw);
                                //algo 2 rule #2 - soften changing bunker conditions to the extreme range
                                if (REACT_APP_ALGO_SOFTEN_CONF && bunkerConditions === 4) {
                                    weight = weight / 2;
                                }
                                shotWeighting = new ShotWeighting(rankOrder, shotNumber, weight);
                                addShotToGrindCollection(loft.loft, loftGrindCalculations, recommendedGrind, shotWeighting);
                            }

                            if (bunkerConditions > 1) {
                                shotNumber = 7
                                recommendedGrind = calculateGrind(loftForCalculations, divotType, swingTypeBunker, swingTypeOpenFace, fairwayConditions, bunkerConditions - 1, bunkerConfidence, avgHandicapLT10, avgHandicapLT5);
                                //save the weighted shot data
                                weight = calculateWeight(shotNumber, rankOrder, weightAmountRaw);
                                //algo 2 rule #2 - soften changing bunker conditions to the extreme range
                                if (REACT_APP_ALGO_SOFTEN_CONF && bunkerConditions === 2) {
                                    weight = weight / 2;
                                }
                                shotWeighting = new ShotWeighting(rankOrder, shotNumber, weight);
                                addShotToGrindCollection(loft.loft, loftGrindCalculations, recommendedGrind, shotWeighting);
                            }
                        }

                        //Shot #8,9 - is this shot used for bunker play? If so, move bunker confidence up/down by 1.
                        if (swingTypeBunker) {
                            if (bunkerConfidence < 5) {
                                shotNumber = 8
                                recommendedGrind = calculateGrind(loftForCalculations, divotType, swingTypeBunker, swingTypeOpenFace, fairwayConditions, bunkerConditions, bunkerConfidence + 1, avgHandicapLT10, avgHandicapLT5);
                                //save the weighted shot data
                                weight = calculateWeight(shotNumber, rankOrder, weightAmountRaw);
                                //algo rule #2 - soften changing bunker confidence to the extreme range
                                if (REACT_APP_ALGO_SOFTEN_CONF && bunkerConfidence === 4) {
                                    weight = weight / 2;
                                }
                                shotWeighting = new ShotWeighting(rankOrder, shotNumber, weight);
                                addShotToGrindCollection(loft.loft, loftGrindCalculations, recommendedGrind, shotWeighting);
                            }

                            if (bunkerConfidence > 1) {
                                shotNumber = 9
                                recommendedGrind = calculateGrind(loftForCalculations, divotType, swingTypeBunker, swingTypeOpenFace, fairwayConditions, bunkerConditions, bunkerConfidence - 1, avgHandicapLT10, avgHandicapLT5);
                                //save the weighted shot data
                                weight = calculateWeight(shotNumber, rankOrder, weightAmountRaw);
                                //algo rule #2 - soften changing bunker confidence to the extreme range
                                if (REACT_APP_ALGO_SOFTEN_CONF && bunkerConfidence === 2) {
                                    weight = weight / 2;
                                }
                                shotWeighting = new ShotWeighting(rankOrder, shotNumber, weight);
                                addShotToGrindCollection(loft.loft, loftGrindCalculations, recommendedGrind, shotWeighting);
                            }
                        }

                    };
                }
            })
        })

    //every loft now has a set of recommended grinds and weights assigned. 
    //go through every shot combo for this loft and get the grand total of all weights for the shots.
    golfer.selectedLofts.forEach(loft => {
        let grindWeighting = loftGrindCalculations.get(loft.loft)!;

        //if we treated 56 loft like 58 loft, than convert the 58 grind weightings to 56 grind weightings
        if (REACT_APP_ALGO_56_TO_58) {
            var maxLoftUsed = [...golfer.loftsForFitting].sort((a, b) => b.loft > a.loft ? 1 : -1)[0];
            if (maxLoftUsed !== undefined) {
                if (loft.loft === "56" && maxLoftUsed.loft === "56") {
                    let grindsPerLoftTable = GrindTables.getGrindsPerLoft(GrindTables.showSM11(fitter.showSm11));            
                    let grindsPerLoft = grindsPerLoftTable.get(loft.loft);
                    if (grindsPerLoft) {
                        let newGrindWeighting = [] as GrindWeighting[];
                        newGrindWeighting.push(new GrindWeighting(grindsPerLoft[0], grindWeighting[0].totalWeight + grindWeighting[1].totalWeight, 0, []));
                        newGrindWeighting.push(new GrindWeighting(grindsPerLoft[1], grindWeighting[2].totalWeight, 0, []));
                        newGrindWeighting.push(new GrindWeighting(grindsPerLoft[2], grindWeighting[3].totalWeight, 0, []));
                        newGrindWeighting.push(new GrindWeighting(grindsPerLoft[3], grindWeighting[4].totalWeight, 0, []));
                        loftGrindCalculations.set("56", newGrindWeighting);
                        grindWeighting = newGrindWeighting;
                    }
                }
            }
        }

        //loop through every grind and do weight calculations
        if (grindWeighting && grindWeighting.length > 0) {
            let loftTotalWeight = 0;
            grindWeighting.forEach(grindWeight => {
                loftTotalWeight += grindWeight.totalWeight;
                // grindWeight.shotWeightings.forEach(shotWeight => {
                //     //track total weight across all grinds for this loft
                //     loftTotalWeight += shotWeight.weight;
                // })
            })

            //loop through every grinds, use the grand total weight from above to get the percent match
            grindWeighting.forEach(grindWeight => {
                if (grindWeight.totalWeight > 0 && loftTotalWeight > 0) {
                    grindWeight.weightPercent = Math.round(grindWeight.totalWeight / loftTotalWeight * 100);
                }
            })

            //algo 2 rule #5. If a grind is 100%, make it 95% and the next closest grind 5%
            if (REACT_APP_ALGO_REDUCE_100 && grindWeighting.length > 1) {
                //sort the grind weightings by bounce
                grindWeighting.sort((a, b) => a.grind > b.grind ? 1 : -1);
                let index = grindWeighting.findIndex(gw => gw.weightPercent === 100);
                if (index > -1) {
                    var nextIndex = -1;
                    //if first in list, use following grind
                    if (index === 0) {
                        nextIndex = 1;
                    }
                    //if last in the list, use previous grind
                    if (index + 1 === grindWeighting.length) {
                        nextIndex = index - 1;
                    }
                    //if in the middle, adjust based on if it's in first half or last half
                    if (nextIndex === -1) {
                        let pct = ((index + 1) / grindWeighting.length);
                        nextIndex = pct < .51 ? index - 1 : index + 1;
                    }
                    grindWeighting[index].weightPercent = 95;
                    grindWeighting[nextIndex].weightPercent = 5;
                }

            }

            //sort the grinds by percent weight with highest first
            grindWeighting.sort((a, b) => b.weightPercent - a.weightPercent);
        }
    })

    //Done!!! return the grind recommendations
    return loftGrindCalculations;
}

//find the current recommended grind and add the shot weighting to it's collecion of shots
//loftGrindCalculations will be saved via reference
const addShotToGrindCollection = (loft: Loft, loftGrindCalculations: LoftGrindCalculations, bounceGrind: BounceGrind, shotWeighting: ShotWeighting): void => {
    let loftGrindCalculation = loftGrindCalculations.get(loft)!;
    loftGrindCalculation.forEach(lg => {
        if (lg.grind === (bounceGrind.bounce + bounceGrind.grind) as GrindTables.BounceGrindCombos) {
            //found the correct grind, add the current shot to it                    
            lg.shotWeightings.push(shotWeighting);
            //track total weight per grind
            lg.totalWeight = lg.totalWeight + shotWeighting.weight;
        }
    })
}

//format the grinds for display on the forms:
//1. sort the grinds by closest match first
//2. calculate average carry distance
//3. determine if there is a small/large gap between full shots
//4. create the notes for each grind recommendation

//NOTE: This needs to be combined with the loftGrindCalculations function even though this will be an enormous function. Too many calculations are shared between them
//or refactor so that there is a master function which passes different collections between the sub functions.
export const formatGrindsForOutput = (loftGrindCalculations: LoftGrindCalculations, golfer: Golfer, simulator: ISimulator, intl: any): LoftGrindRecommendation[] => {
    let loftGrindRecommendations: LoftGrindRecommendation[] = [];

    const intlAllPurpose = intl.formatMessage({ id: 'notes.all.purpose' });
    const intlYour = intl.formatMessage({ id: 'notes.your' });
    const intlDelivery = intl.formatMessage({ id: 'notes.delivery' });
    const intlCourseConditions = intl.formatMessage({ id: 'notes.course.conditions' });
    const intlBunkersAre = intl.formatMessage({ id: 'notes.bunkers.are' });
    const intlYouHave = intl.formatMessage({ id: 'notes.you.have' });
    const intlConfidence = intl.formatMessage({ id: 'notes.confidence' });
    const intlClubDelivery = intl.formatMessage({ id: 'notes.club.delivery' });
    const intlVersatility = intl.formatMessage({ id: 'notes.versatility' });
    const intlShots = intl.formatMessage({ id: 'notes.shots' });

    //build out attack angle averages for all shot types
    let attackAngleAverages: ShotTypeAttackAngleAverage = new Map([["Full", new AttackAngleAverage(0, 0)], ["Pitch", new AttackAngleAverage(0, 0)], ["Open Face", new AttackAngleAverage(0, 0)]]);
    golfer.loftsForFitting.forEach(loft => {
        loft.practiceShots.forEach(shot => {
            if (shot.status === "closed" && shot.data) {
                let aa_sl = simulator.calculateAttackAngleShaftLean(loft.loft, loft.shotType, shot.data!.attackAngle, shot.data!.dynamicLoft, true, true);
                if (aa_sl.attackAngle !== "--") {
                    //update the attack angle object for the current shot type
                    let attackAngle = attackAngleAverages.get(loft.shotType)!;
                    attackAngle.numberShots++;
                    attackAngle.totalAttackAngle += Number(aa_sl.attackAngle);
                }
            }
        })
    });

    //loop through all the selected lofts and shot types and if there is a recommended grind for it, then add it to the collection
    golfer.selectedLofts.forEach(loft => {
        let grindWeightings = loftGrindCalculations.get(loft.loft);
        if (grindWeightings !== undefined) {
            let newGrindWeightings: GrindWeightingRecommendation[] = [];
            let first = true;
            let selectedBounce = "";
            let selectedGrind = "";
            grindWeightings.forEach(weighting => {
                if (first) {
                    selectedBounce = weighting.grind.substring(0, 2);
                    selectedGrind = weighting.grind.substring(2, 3);
                    first = false;
                }
                newGrindWeightings.push(new GrindWeightingRecommendation(weighting.grind.substring(0, 2), weighting.grind.substring(2, 3), "130 yds", weighting.weightPercent));
            })

            let loftGrindRecommendation = new LoftGrindRecommendation(loftGrindRecommendations.length + 1, loft.loft, selectedBounce, selectedGrind, 0, false, false, false, newGrindWeightings,[]);
            //before saving the grind recommendation to the collection, we need to calculate it's average carry distance for this loft
            loftGrindRecommendation.averageCarryDistance = calculateAverageCarryDistance(loft.loft, golfer.loftsForFitting);
            loftGrindRecommendations.push(loftGrindRecommendation);
        }
    })

    //determine if there is a gap between lofts
    //must be at least two lofts displayed to do this calculation
    if (loftGrindRecommendations.length >= 2) {
        //ignore the final loft b/c it is last and can't have a gapping b/c there is not next loft
        for (let ix = 0; ix < loftGrindRecommendations.length - 1; ix++) {
            //first check if either carry distance is zero then set it to a no gap line b/c the other calculations won't be accurate
            //otherwise do a normal carry distance calculation
            // if (loftGrindRecommendations[ix].averageCarryDistance === 0 || loftGrindRecommendations[ix+1].averageCarryDistance === 0){
            //     loftGrindRecommendations[ix].gapLine = true;
            // } else 

            if (loftGrindRecommendations[ix].averageCarryDistance - loftGrindRecommendations[ix + 1].averageCarryDistance < 10 && loftGrindRecommendations[ix + 1].averageCarryDistance !== 0) {
                loftGrindRecommendations[ix].gapSmall = true;
            } else if (loftGrindRecommendations[ix].averageCarryDistance - loftGrindRecommendations[ix + 1].averageCarryDistance > 15 && loftGrindRecommendations[ix + 1].averageCarryDistance !== 0) {
                loftGrindRecommendations[ix].gapLarge = true;
            } else {
                loftGrindRecommendations[ix].gapLine = true;
            }
        }
    }

    //create the notes associated with each recommendation
    loftGrindRecommendations.forEach(loft => {
        //get the loft object from the 'selectedLofts' collection. This contains which shots are in use and their priority
        let selectedLoft = golfer.selectedLofts.find(sl => sl.loft === loft.loft)!;
        //get the data for the priority shot type        
        let priorityShotType = "";
        let priorityDivotType = "";
        let priorityShot = selectedLoft.shots.find(sl => sl.priority === 0 && sl.selected);
        if (priorityShot) {
            priorityShotType = priorityShot.shotType.toLowerCase();
            let attackAngle = attackAngleAverages.get(priorityShot.shotType);
            if (attackAngle && attackAngle.totalAttackAngle !== 0) {
                priorityDivotType = calculateDivotType(attackAngle.totalAttackAngle / attackAngle.numberShots).toLowerCase();
            }
        }
        //get the data for the 2nd priority shot type        
        let secondPriorityShotType = "";
        let secondPriorityDivotType = "";
        let secondPriorityShot = selectedLoft.shots.find(sl => sl.priority === 1 && sl.selected);
        if (secondPriorityShot) {
            secondPriorityShotType = secondPriorityShot.shotType.toLowerCase();
            let attackAngle = attackAngleAverages.get(secondPriorityShot.shotType);
            if (attackAngle && attackAngle.totalAttackAngle !== 0) {
                secondPriorityDivotType = calculateDivotType(attackAngle.totalAttackAngle / attackAngle.numberShots).toLowerCase();
            }
        }



        if (["44", "46", "48"].includes(loft.loft)) {
            loft.notes.push(new NoteReason("loft default", intlAllPurpose));
        }
        //club delivery
        if (["50", "52", "54", "56", "58", "60"].includes(loft.loft)) {
            if (priorityShotType !== "" && priorityDivotType !== "") {
                loft.notes.push(new NoteReason("first priority shot", `${intlYour} ${intl.formatMessage({ id: 'shot.selection.' + priorityShotType.replace(' ', '').toLowerCase() })} ${intlDelivery} ${intl.formatMessage({ id: 'now.hitting.' + priorityDivotType })}`));
            }
            loft.notes.push(new NoteReason("club delivery", `${intlCourseConditions} ${intl.formatMessage({ id: 'golfer.info.' + golfer.courseConditions.toLowerCase().replace(' ', '.') })}`));
        }
        //bunker use
        if (["54", "56", "58", "60"].includes(loft.loft)) {
            let bunkerUse = selectedLoft.shots.find(bl => bl.shotType === "Bunker" && bl.selected);
            if (bunkerUse) {
                loft.notes.push(new NoteReason("bunker use", `${intlBunkersAre} ${intl.formatMessage({ id: 'golfer.info.' + golfer.bunkerConditions.toLowerCase().replace(' ', '.') })} ${intlYouHave} ${intl.formatMessage({ id: 'golfer.info.' + golfer.confidence.toLowerCase().replace(' ', '.') })} ${intlConfidence}`));
            } else {
                // no bunker shot, so display the #2 priority shot instead
                if (secondPriorityShotType !== "" && secondPriorityDivotType !== "") {
                    loft.notes.push(new NoteReason("second priority shot", `${intlYour} ${intl.formatMessage({ id: 'shot.selection.' + secondPriorityShotType.replace(' ', '').toLowerCase() })} ${intlClubDelivery} ${intl.formatMessage({ id: 'now.hitting.' + secondPriorityDivotType })}`));
                }
            }
        }
        if (loft.loft === "62") {
            loft.notes.push(new NoteReason("loft default", intlVersatility));
        }

        //show shots taken for the loft, sort by priority
        let selectedShotTypes = selectedLoft.shots.filter(sl => sl.selected).sort((a, b) => a.priority - b.priority).map(sl => intl.formatMessage({ id: 'shot.selection.' + sl.shotType.replace(' ', '').toLowerCase() })).join(', ');
        loft.notes.push(new NoteReason("", intlShots + " " + selectedShotTypes));

    })

    return loftGrindRecommendations;
}

//calculate average carry distance by looping through all practice shots for a specific loft
//return it as a printable string (200 yds) or empty string if zero
//updated by reference
const calculateAverageCarryDistance = (loft: Loft, loftsForFitting: LoftForFitting[]): number => {
    let totalCarryDistance = 0;
    let numberShots = 0;
    let averageCarryDistance = 0;
    loftsForFitting.forEach(fitting => {
        if (fitting.loft === loft && fitting.shotType === "Full") {
            fitting.practiceShots.forEach(shot => {
                if (shot.status === "closed" && shot.data) {
                    totalCarryDistance += shot.data.carryDistance;
                    numberShots++;
                }
            })
        }
    })
    if (totalCarryDistance > 0) {
        averageCarryDistance = Math.round(totalCarryDistance / numberShots);
    }
    return averageCarryDistance;
}

//use the weight and ranking arrays to calculate the weight
const calculateWeight = (shotNumber: number, rankOrder: number, weightAmountRaw: number[]): number => {
    let weight = 0;
    if (shotNumber < weightAmountRaw.length) {
        weight = weightAmountRaw[shotNumber] * RankOrderMultiplier[rankOrder];
    }
    return weight;
}

export const calculateDivotType = (attackAngle: number): string => {
    let divotType = "";
    if (attackAngle > -4) {
        divotType = "Shallow";
    } else if (attackAngle > -8) {
        divotType = "Neutral"
    } else {
        divotType = "Steep"
    }

    return divotType;
}

export const calculateGrind = (loft: Loft, divotType: string, swingTypeBunker: boolean, swingTypeOpenFace: boolean,
    fairwayConditions: number, bunkerConditions: number, bunkerConfidence: number, avgHandicapLT10: boolean, avgHandicapLT5: boolean): BounceGrind => {
    let bounceGrind = {} as BounceGrind;

    //easy peasy for basic lofts that have only one bounce grind option
    if (loft === "44" || loft === "46" || loft === "48") {
        bounceGrind = new BounceGrind("10", "F");
    }
    if (loft === "62") {
        bounceGrind = new BounceGrind("08", "M")
    }

    //if not the easy loft above, go through the full process to use the grind tables
    if (bounceGrind.bounce === undefined) {
        let filter = new GrindTables.Filter("", "", divotType, fairwayConditions, swingTypeBunker, swingTypeOpenFace, bunkerConditions, bunkerConfidence, avgHandicapLT10, avgHandicapLT5);

        //algo 2 rule for T-grind and a few minor grind changes
        let loftGrindsMap = GrindTables.getLoftGrindsMap();
            bounceGrind = getBounceGrindFromTables(filter, loftGrindsMap.get(loft)!);
    }

    return bounceGrind;
}

export function getBounceGrindFromTables(filter: GrindTables.Filter, grindData: GrindTables.Filter[]): BounceGrind {
    let bounceGrind = new BounceGrind("", "");
    let found = false;

    for (let ix = 0; ix < grindData.length && !found; ix++) {
        if (grindData[ix].divotType === filter.divotType &&
            (grindData[ix].fairwayConditions === undefined || grindData[ix].fairwayConditions === filter.fairwayConditions) &&
            (grindData[ix].swingTypeBunker === undefined || grindData[ix].swingTypeBunker === filter.swingTypeBunker) &&
            (grindData[ix].swingTypeOpenFace === undefined || grindData[ix].swingTypeOpenFace === filter.swingTypeOpenFace) &&
            (grindData[ix].bunkerConditions === undefined || grindData[ix].bunkerConditions === filter.bunkerConditions) &&
            (grindData[ix].bunkerConfidence === undefined || grindData[ix].bunkerConfidence === filter.bunkerConfidence) &&
            (grindData[ix].avgHandicapLT10 === undefined || grindData[ix].avgHandicapLT10 === filter.avgHandicapLT10) &&
            (grindData[ix].avgHandicapLT5 === undefined || grindData[ix].avgHandicapLT5 === filter.avgHandicapLT5)
        ) {
            bounceGrind = new BounceGrind(grindData[ix].bounce, grindData[ix].grind);
            found = true;
        }
    }
    return bounceGrind;
}


